import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, Capabilities } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { useMe } from '@/features/auth/useAuth'
import { Select } from '@/components/Select'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from '@/features/inventory/shared'
import { fmtDate, fmtEur, fmtNumber, useT } from '@/i18n'
import { billingMessages } from '@/i18n/messages/billing'
import { cycleLabel, DueBadge } from './BillingPage'
import { ITEMS, QTY_SHORT, type Contract, type ContractDetail, type Price, type Session, type StatementDetail, type StatementSummary } from './types'

type T = ReturnType<typeof useBillingT>
function useBillingT() { return useT(billingMessages) }
const today = () => new Date().toISOString().slice(0, 10)
const errText = (e: unknown) => (e instanceof ApiError ? e.message : String(e))

/** One client contract: terms, prices, the unbilled work with its documents, and the statements. */
export function ContractPage() {
  const t = useBillingT()
  const { id } = useParams<{ id: string }>()
  const { data: me } = useMe()
  const canManage = me?.capabilities.includes(Capabilities.ManageBilling) ?? false
  const qc = useQueryClient()
  const { data, isPending } = useQuery<ContractDetail>({ queryKey: ['billing-contract', id], queryFn: () => api.get<ContractDetail>(`/api/billing/contracts/${id}`) })
  const [dialog, setDialog] = useState<'terms' | 'prices' | 'build' | null>(null)
  const [statement, setStatement] = useState<string | null>(null)
  const [waiving, setWaiving] = useState<Session | null>(null)
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['billing-contract', id] }); void qc.invalidateQueries({ queryKey: ['billing-overview'] }) }

  const loader = usePageLoader(isPending, t('loading'))
  if (loader) return loader
  if (!data) return null
  const k = data.contract
  const draft = data.statements.find((s) => s.status === 'draft')

  return (
    <div className="space-y-5">
      <Link to="/billing" className="text-xs text-neutral-500 hover:underline">← {t('backToOverview')}</Link>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{k.projectName ?? '—'}</h1>
          <p className="mt-1 text-sm text-neutral-500">{k.clientName} · {k.projectCode} · {cycleLabel(t, k.cycle)}</p>
        </div>
        <div className="flex items-center gap-3">
          <DueBadge t={t} due={data.nextDue} />
          {canManage && <PrimaryButton onClick={() => setDialog('build')} disabled={data.readyValue === 0 && !data.unbilled.some((s) => s.ready)}>{t('newStatement')}</PrimaryButton>}
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label={t('tileReady')} value={fmtEur(data.readyValue)} sub={t('crewDays', { n: data.unbilled.filter((s) => s.ready).length })} />
        <Tile label={t('tileBlocked')} value={fmtEur(data.blockedValue)} sub={t('crewDays', { n: data.unbilled.filter((s) => !s.ready).length })} tone="warn" />
        <TermsCard t={t} k={k} canManage={canManage} onEdit={() => setDialog('terms')} />
        <PricesCard t={t} prices={data.prices} canManage={canManage} onEdit={() => setDialog('prices')} />
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('statements')}</h2>
        {data.statements.length === 0 ? <p className="text-sm text-neutral-500">—</p> : (
          <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                <th className="px-3 py-2 font-medium">{t('number')}</th><th className="px-3 py-2 font-medium">{t('period')}</th><th className="px-3 py-2 text-right font-medium">{t('net')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('payable')}</th><th className="px-3 py-2 font-medium">{t('colStatus')}</th><th className="px-3 py-2 font-medium">{t('erp')}</th>
              </tr></thead>
              <tbody>
                {data.statements.map((s) => (
                  <tr key={s.id} onClick={() => setStatement(s.id)} className="cursor-pointer border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40">
                    <td className="px-3 py-2 font-mono text-xs font-semibold">{s.number ?? t('draft')}</td>
                    <td className="tabular px-3 py-2 text-xs">{fmtDate(s.periodFrom)} – {fmtDate(s.periodTo)}</td>
                    <td className="tabular px-3 py-2 text-right">{fmtEur(s.net)}</td>
                    <td className="tabular px-3 py-2 text-right font-semibold">{fmtEur(s.payableNet)}</td>
                    <td className="px-3 py-2"><StatusChip t={t} s={s} /></td>
                    <td className="px-3 py-2 text-xs">{s.erpInvoiceNo ? `${s.erpInvoiceNo} · ${fmtDate(s.erpInvoiceDate)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('work')}</h2>
        {data.unbilled.length === 0 ? <p className="text-sm text-neutral-500">{t('nothingReady')}</p> : (
          <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                <th className="px-3 py-2 font-medium">{t('colDate')}</th><th className="px-3 py-2 font-medium">{t('colCrew')}</th><th className="px-3 py-2 font-medium">{t('colQty')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('colValue')}</th><th className="px-3 py-2 font-medium">{t('colDocs')}</th><th className="px-3 py-2 font-medium">{t('colStatus')}</th>
              </tr></thead>
              <tbody>
                {data.unbilled.slice(0, 400).map((s) => (
                  <tr key={s.sessionId} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                    <td className="tabular whitespace-nowrap px-3 py-1.5 text-xs">{fmtDate(s.workDate)}</td>
                    <td className="px-3 py-1.5 text-xs">{s.crew}<div className="max-w-[16rem] truncate text-neutral-500">{s.streets}</div></td>
                    <td className="tabular px-3 py-1.5 text-xs">{Object.entries(s.qty).map(([k, v]) => `${fmtNumber(v, 1)} ${QTY_SHORT[k] ?? k}`).join(' · ')}</td>
                    <td className="tabular px-3 py-1.5 text-right">{fmtEur(s.value)}</td>
                    <td className="px-3 py-1.5 text-xs"><Docs t={t} s={s} /></td>
                    <td className="px-3 py-1.5 text-xs">
                      {s.ready
                        ? <span className="font-medium text-green-700 dark:text-green-300">{s.waiverReason ? t('waived') : t('ready')}{s.onDraft ? ` · ${t('onDraft')}` : ''}</span>
                        : <span className="font-medium text-amber-800 dark:text-amber-300">{t('missing', { what: s.missing.map((m) => t(m as 'photos' | 'rtk' | 'lidar')).join(', ') })}</span>}
                      {canManage && !s.ready && <button type="button" onClick={() => setWaiving(s)} className="ml-2 text-brand-700 hover:underline dark:text-brand-300">{t('waive')}</button>}
                      {canManage && s.waiverReason && <button type="button" onClick={async () => { await api.del(`/api/billing/contracts/${k.id}/waivers/${s.sessionId}`); refresh() }} className="ml-2 text-neutral-500 hover:underline">{t('unwaive')}</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {dialog === 'terms' && <TermsDialog t={t} k={k} onClose={() => setDialog(null)} onSaved={refresh} />}
      {dialog === 'prices' && <PricesDialog t={t} k={k} prices={data.prices} onClose={() => setDialog(null)} onSaved={refresh} />}
      {dialog === 'build' && <BuildDialog t={t} k={k} defaultTo={data.nextDue?.due ? data.nextDue.to : today()} hasDraft={Boolean(draft)}
        onClose={() => setDialog(null)} onBuilt={(sid) => { refresh(); setStatement(sid) }} />}
      {waiving && <WaiveDialog t={t} contractId={k.id} session={waiving} onClose={() => setWaiving(null)} onSaved={refresh} />}
      {statement && <StatementPanel t={t} id={statement} canManage={canManage} onClose={() => setStatement(null)} onChanged={refresh} />}
    </div>
  )
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'warn' | undefined }) {
  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className={`tabular mt-1 text-2xl font-semibold ${tone === 'warn' ? 'text-amber-800 dark:text-amber-300' : ''}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-neutral-500">{sub}</div>}
    </div>
  )
}

function Docs({ t, s }: { t: T; s: Session }) {
  const d = (ok: boolean, label: string) => <span className={`mr-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${ok ? 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300' : 'bg-neutral-100 text-neutral-400 line-through dark:bg-neutral-800'}`}>{label}</span>
  return <>{d(s.photos, t('photos'))}{d(s.rtk, t('rtk'))}{d(s.lidar, t('lidar'))}</>
}

function StatusChip({ t, s }: { t: T; s: StatementSummary }) {
  const cls = s.status === 'issued' ? 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300' : s.status === 'void' ? 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200'
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{s.status === 'issued' ? t('issued') : s.status === 'void' ? t('void') : t('draft')}</span>
}

function TermsCard({ t, k, canManage, onEdit }: { t: T; k: Contract; canManage: boolean; onEdit: () => void }) {
  const req = [k.requirePhotos && t('photos'), k.requireRtk && t('rtk'), k.requireLidar && t('lidar')].filter(Boolean).join(' · ')
  return (
    <div className="rounded-2xl bg-white p-4 text-xs ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="mb-1 flex items-center justify-between"><span className="text-xs text-neutral-500">{t('terms')}</span>
        {canManage && <button type="button" onClick={onEdit} className="font-medium text-brand-700 hover:underline dark:text-brand-300">{t('editTerms')}</button>}</div>
      <div>{t('retention')} {fmtNumber(k.retentionPct, 2)} % · {k.reverseCharge13b ? '§13b' : `${t('vat')} ${fmtNumber(k.vatRate, 2)} %`} · {t('paymentDays')} {t('days', { n: k.paymentDays })}</div>
      <div className="mt-1 text-neutral-500">{t('docsRequired')}: {req || '—'}</div>
      <div className="text-neutral-500">{t('docsFrom')}: {fmtDate(k.docsRequiredFrom)}</div>
      <div className="text-neutral-500">{t('billingFrom')}: {fmtDate(k.billingFrom)}</div>
    </div>
  )
}

function PricesCard({ t, prices, canManage, onEdit }: { t: T; prices: Price[]; canManage: boolean; onEdit: () => void }) {
  return (
    <div className="rounded-2xl bg-white p-4 text-xs ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="mb-1 flex items-center justify-between"><span className="text-xs text-neutral-500">{t('prices')}</span>
        {canManage && <button type="button" onClick={onEdit} className="font-medium text-brand-700 hover:underline dark:text-brand-300">{t('editPrices')}</button>}</div>
      {prices.length === 0 ? <div className="text-neutral-500">{t('noPrice')}</div> : prices.map((p) => (
        <div key={p.key} className="flex justify-between gap-2"><span>{p.label}</span><span className="tabular">{fmtEur(p.unitPrice, 2)} / {p.unit} <span className="text-neutral-400">{p.source === 'au' ? t('fromAu') : t('manual')}</span></span></div>
      ))}
    </div>
  )
}

function TermsDialog({ t, k, onClose, onSaved }: { t: T; k: Contract; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ ...k, retentionPct: String(k.retentionPct), vatRate: String(k.vatRate), paymentDays: String(k.paymentDays), note: k.note ?? '' })
  const [err, setErr] = useState<string | null>(null)
  const save = async () => {
    try {
      await api.put(`/api/billing/contracts/${k.id}`, { clientName: f.clientName, cycle: f.cycle, retentionPct: Number(f.retentionPct.replace(',', '.')), reverseCharge13b: f.reverseCharge13b,
        vatRate: Number(f.vatRate.replace(',', '.')), paymentDays: Number(f.paymentDays), requirePhotos: f.requirePhotos, requireRtk: f.requireRtk, requireLidar: f.requireLidar,
        docsRequiredFrom: f.docsRequiredFrom, billingFrom: f.billingFrom, note: f.note || null })
      onSaved(); onClose()
    } catch (e) { setErr(errText(e)) }
  }
  const check = (key: 'reverseCharge13b' | 'requirePhotos' | 'requireRtk' | 'requireLidar', label: string) => (
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.checked })} className="size-4" />{label}</label>)
  return (
    <Dialog title={t('editTerms')} onClose={onClose} footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => void save()}>{t('save')}</PrimaryButton></>}>
      <Field label={t('client')}><input value={f.clientName} onChange={(e) => setF({ ...f, clientName: e.target.value })} className={INPUT} /></Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('cycle')}><Select value={f.cycle} onChange={(v) => setF({ ...f, cycle: v as Contract['cycle'] })}
          options={[{ value: 'monthly', label: t('monthly') }, { value: 'biweekly', label: t('biweekly') }, { value: 'weekly', label: t('weekly') }]} /></Field>
        <Field label={`${t('retention')} (%)`}><input inputMode="decimal" value={f.retentionPct} onChange={(e) => setF({ ...f, retentionPct: e.target.value })} className={INPUT} /></Field>
        <Field label={`${t('vat')} (%)`}><input inputMode="decimal" value={f.vatRate} onChange={(e) => setF({ ...f, vatRate: e.target.value })} className={INPUT} /></Field>
        <Field label={`${t('paymentDays')}`}><input inputMode="numeric" value={f.paymentDays} onChange={(e) => setF({ ...f, paymentDays: e.target.value })} className={INPUT} /></Field>
      </div>
      {check('reverseCharge13b', t('reverseCharge'))}
      <Field label={t('docsRequired')}><div className="flex flex-wrap gap-4">{check('requirePhotos', t('photos'))}{check('requireRtk', t('rtk'))}{check('requireLidar', t('lidar'))}</div></Field>
      <Field label={t('docsFrom')}><input type="date" value={f.docsRequiredFrom} onChange={(e) => setF({ ...f, docsRequiredFrom: e.target.value })} className={INPUT} /></Field>
      <Field label={t('billingFrom')} hint={t('billingFromHint')}><input type="date" value={f.billingFrom} onChange={(e) => setF({ ...f, billingFrom: e.target.value })} className={INPUT} /></Field>
      <Field label={t('note')}><input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={INPUT} /></Field>
      {err && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{err}</p>}
    </Dialog>
  )
}

function PricesDialog({ t, k, prices, onClose, onSaved }: { t: T; k: Contract; prices: Price[]; onClose: () => void; onSaved: () => void }) {
  const [rows, setRows] = useState(ITEMS.map((i) => { const p = prices.find((x) => x.key === i.key); return { key: i.key, label: p?.label ?? i.label, unit: i.unit, price: p ? String(p.unitPrice) : '', oz: p?.lvOz ?? '' } }))
  const [err, setErr] = useState<string | null>(null)
  const save = async () => {
    try {
      await api.put(`/api/billing/contracts/${k.id}/prices`, rows.filter((r) => r.price.trim() !== '').map((r) => ({ key: r.key, label: r.label, unitPrice: Number(r.price.replace(',', '.')), lvOz: r.oz || null })))
      onSaved(); onClose()
    } catch (e) { setErr(errText(e)) }
  }
  return (
    <Dialog title={t('editPrices')} onClose={onClose} footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => void save()}>{t('save')}</PrimaryButton></>}>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.key} className="grid grid-cols-[minmax(0,1fr)_6rem_5rem] items-center gap-2">
            <input value={r.label} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className={INPUT} aria-label={t('item')} />
            <input inputMode="decimal" placeholder={`€ / ${r.unit}`} value={r.price} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} className={`${INPUT} text-right`} aria-label={t('unitPrice')} />
            <input placeholder={t('lvOz')} value={r.oz} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, oz: e.target.value } : x)))} className={INPUT} aria-label={t('lvOz')} />
          </div>
        ))}
      </div>
      {err && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{err}</p>}
    </Dialog>
  )
}

function BuildDialog({ t, k, defaultTo, hasDraft, onClose, onBuilt }: { t: T; k: Contract; defaultTo: string; hasDraft: boolean; onClose: () => void; onBuilt: (id: string) => void }) {
  const [to, setTo] = useState(defaultTo > today() ? today() : defaultTo)
  const [kind, setKind] = useState<'progress' | 'final'>('progress')
  const [err, setErr] = useState<string | null>(null)
  const build = async () => {
    try { const r = await api.post<{ id: string }>(`/api/billing/contracts/${k.id}/statements`, { periodTo: to, kind }); onClose(); onBuilt(r.id) }
    catch (e) { setErr(errText(e)) }
  }
  return (
    <Dialog title={t('newStatement')} hint={hasDraft ? t('draft') : undefined} onClose={onClose} footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={() => void build()}>{t('newStatement')}</PrimaryButton></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('periodTo')}><input type="date" value={to} max={today()} onChange={(e) => setTo(e.target.value)} className={INPUT} /></Field>
        <Field label={t('kind')}><Select value={kind} onChange={(v) => setKind(v as 'progress' | 'final')}
          options={[{ value: 'progress', label: t('progress') }, { value: 'final', label: t('final') }]} /></Field>
      </div>
      {err && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{err}</p>}
    </Dialog>
  )
}

function WaiveDialog({ t, contractId, session, onClose, onSaved }: { t: T; contractId: string; session: Session; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const save = async () => {
    try { await api.post(`/api/billing/contracts/${contractId}/waivers`, { sessionId: session.sessionId, reason }); onSaved(); onClose() } catch (e) { setErr(errText(e)) }
  }
  return (
    <Dialog title={t('waive')} hint={`${fmtDate(session.workDate)} · ${session.crew ?? ''}`} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton disabled={!reason.trim()} onClick={() => void save()}>{t('save')}</PrimaryButton></>}>
      <Field label={t('waiveReason')}><input value={reason} onChange={(e) => setReason(e.target.value)} className={INPUT} autoFocus /></Field>
      {err && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{err}</p>}
    </Dialog>
  )
}

/** The statement: cumulative lines, totals, and its actions (issue, void, invoice number, PDF, Aufmaßblatt). */
function StatementPanel({ t, id, canManage, onClose, onChanged }: { t: T; id: string; canManage: boolean; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient()
  const { data } = useQuery<StatementDetail>({ queryKey: ['billing-statement', id], queryFn: () => api.get<StatementDetail>(`/api/billing/statements/${id}`) })
  const [mode, setMode] = useState<'void' | 'erp' | null>(null)
  const [text, setText] = useState(''); const [date, setDate] = useState(today())
  const [err, setErr] = useState<string | null>(null)
  if (!data) return null
  const s = data.statement, k = data.contract
  const act = async (fn: () => Promise<unknown>, close = false) => {
    setErr(null)
    try { await fn(); onChanged(); void qc.invalidateQueries({ queryKey: ['billing-statement', id] }); setMode(null); setText(''); if (close) onClose() } catch (e) { setErr(errText(e)) }
  }
  return (
    <Dialog title={`${s.kind === 'final' ? t('final') : t('progress')} ${s.number ?? `· ${t('draft')}`}`} hint={`${k.clientName} · ${fmtDate(s.periodFrom)} – ${fmtDate(s.periodTo)} · ${t('crewDays', { n: s.sessions })}`}
      onClose={onClose} footer={<GhostButton onClick={onClose}>{t('close')}</GhostButton>}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-neutral-200 text-left text-neutral-500 dark:border-neutral-800">
            <th className="py-1.5 font-medium">{t('item')}</th><th className="py-1.5 text-right font-medium">{t('unitPrice')}</th><th className="py-1.5 text-right font-medium">{t('colPrev')}</th>
            <th className="py-1.5 text-right font-medium">{t('colThis')}</th><th className="py-1.5 text-right font-medium">{t('colToDate')}</th><th className="py-1.5 text-right font-medium">{t('colAmount')}</th>
          </tr></thead>
          <tbody>
            {data.lines.map((l) => (
              <tr key={l.itemKey} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                <td className="py-1.5">{l.label} <span className="text-neutral-400">{l.unit}</span></td>
                <td className="tabular py-1.5 text-right">{l.unitPrice != null ? fmtEur(l.unitPrice, 2) : <span className="text-amber-700">{t('noPrice')}</span>}</td>
                <td className="tabular py-1.5 text-right">{fmtNumber(l.qtyPrevious, 2)}</td>
                <td className="tabular py-1.5 text-right font-semibold">{fmtNumber(l.qtyPeriod, 2)}</td>
                <td className="tabular py-1.5 text-right">{fmtNumber(l.qtyToDate, 2)}</td>
                <td className="tabular py-1.5 text-right">{fmtEur(l.amount, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="ml-auto grid w-64 grid-cols-2 gap-x-3 gap-y-0.5 text-sm">
        <dt className="text-neutral-500">{t('net')}</dt><dd className="tabular text-right">{fmtEur(s.net, 2)}</dd>
        <dt className="text-neutral-500">{t('retentionAmt')} {fmtNumber(k.retentionPct, 2)} %</dt><dd className="tabular text-right">−{fmtEur(s.retention, 2)}</dd>
        <dt className="font-semibold">{t('payable')}</dt><dd className="tabular text-right font-semibold">{fmtEur(s.payableNet, 2)}</dd>
        <dt className="text-neutral-500">{t('vatAmt')}</dt><dd className="tabular text-right">{fmtEur(s.vat, 2)}</dd>
        <dt className="font-semibold">{t('gross')}</dt><dd className="tabular text-right font-semibold">{fmtEur(s.gross, 2)}</dd>
      </dl>
      {k.reverseCharge13b && <p className="text-xs text-neutral-500">{t('reverseNote')}</p>}
      {s.unpricedItems > 0 && <p className="text-xs font-medium text-amber-800 dark:text-amber-300">{t('unpriced', { n: s.unpricedItems })}</p>}
      {s.status === 'void' && <p className="text-xs font-medium text-red-700">{t('void')}: {s.voidReason}</p>}
      {s.erpInvoiceNo && <p className="text-xs">{t('erp')}: <b>{s.erpInvoiceNo}</b> · {fmtDate(s.erpInvoiceDate)}</p>}
      <div className="flex flex-wrap gap-2">
        <a href={`/api/billing/statements/${id}/statement.pdf`} className="rounded-lg bg-neutral-100 px-3 py-1.5 text-xs font-semibold hover:bg-neutral-200 dark:bg-neutral-800">{t('pdf')}</a>
        <a href={`/api/billing/statements/${id}/aufmassblatt.csv`} className="rounded-lg bg-neutral-100 px-3 py-1.5 text-xs font-semibold hover:bg-neutral-200 dark:bg-neutral-800">{t('csv')}</a>
        {canManage && s.status === 'draft' && <>
          <PrimaryButton onClick={() => void act(() => api.post(`/api/billing/statements/${id}/issue`))}>{t('issue')}</PrimaryButton>
          <GhostButton onClick={() => void act(() => api.del(`/api/billing/statements/${id}`), true)}>{t('deleteDraft')}</GhostButton>
        </>}
        {canManage && s.status === 'issued' && <>
          <GhostButton onClick={() => setMode('erp')}>{t('recordErp')}</GhostButton>
          <GhostButton onClick={() => setMode('void')}>{t('voidIt')}</GhostButton>
        </>}
      </div>
      {s.status === 'draft' && <p className="text-xs text-neutral-500">{t('issueHint')}</p>}
      {mode === 'erp' && (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_10rem_auto]">
          <input placeholder={t('invoiceNo')} value={text} onChange={(e) => setText(e.target.value)} className={INPUT} />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={INPUT} aria-label={t('invoiceDate')} />
          <PrimaryButton disabled={!text.trim()} onClick={() => void act(() => api.post(`/api/billing/statements/${id}/erp`, { invoiceNo: text, invoiceDate: date }))}>{t('save')}</PrimaryButton>
        </div>
      )}
      {mode === 'void' && (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          <input placeholder={t('voidReason')} value={text} onChange={(e) => setText(e.target.value)} className={INPUT} />
          <PrimaryButton disabled={!text.trim()} onClick={() => void act(() => api.post(`/api/billing/statements/${id}/void`, { reason: text }))}>{t('voidIt')}</PrimaryButton>
        </div>
      )}
      {err && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{err}</p>}
    </Dialog>
  )
}
